import { css } from 'aphrodite';
import * as React from 'react';
import { useEffect } from 'react';
import { IPhoneCall } from '../../../extViewmodels';
import { getDisplayName } from '../../../models/NameUtils';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { usePhoneCall } from '../../entities/PhoneCall/usePhoneCall';
import { getBoolFromStatus } from '../../entities/utils/getBoolFromStatus';
import { Group } from '../leadView/components/Group';
import { CallDetailsAudioPlayer } from './components/CallDetailsAudioPlayer';
import { CallDetailsCard } from './components/CallDetailsCard';
import { CallDetailsCategory } from './components/CallDetailsCategory';
import { CallDetailsNotes } from './components/CallDetailsNotes';
import { CallDetailsTitle } from './components/CallDetailsTitle';
import { CallDetailsTranscription } from './components/CallDetailsTranscription';
import type { RecordingPlaybackSurface } from '../../services/recordingPlayback';
import { styleSheet } from './styles';

interface IProps {
	autoplay?: boolean;
	isMobile?: boolean;
	onOpenAIFeedback?: (phoneCallId: string, transcriptionId: string) => void;
	recentCallId: string;
	surface?: RecordingPlaybackSurface;
}

export const CallDetailsDrawerContent: React.FC<IProps> = ({
	autoplay = false,
	isMobile = false,
	onOpenAIFeedback,
	recentCallId,
	surface = 'callLogDrawer',
}) => {
	const { data, empty, getById, status } = usePhoneCall<IPhoneCall>();
	const { isLoading, isEmpty } = getBoolFromStatus({ empty, status });

	useEffect(() => {
		if (recentCallId) {
			getById(recentCallId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [recentCallId]);

	const renderEmpty = <div className={css(styleSheet.emptyContent)}>Cannot find Phone Call information.</div>;

	const hasRecording = data?.recording?.publicUrl;
	const haveEnoughDuration = data?.duration >= 3;

	const renderContent = (
		<>
			<CallDetailsTitle isMobile={isMobile} companyId={data?.companyId} />
			<Group vertical={true} spacingSize={20} className={css(styleSheet.sectionWrapper)}>
				<CallDetailsCard
					callDate={data?.creationDate}
					callStatus={data?.callStatus}
					sdrName={getDisplayName(data?.creator)}
					sdrPhoneNumber={data?.creatorPhoneNumber?.standard}
				/>
				{hasRecording && (
					<CallDetailsAudioPlayer
						autoplay={autoplay}
						contactId={data?.contactIds?.[0]}
						creationDate={data?.creationDate}
						creatorName={getDisplayName(data?.creator)}
						phoneCallId={data?.id}
						phoneNumber={data?.phoneNumber?.standard}
						recordingSource={data?.recording?.publicUrl}
						surface={surface}
					/>
				)}
				<CallDetailsCategory phoneCallCategories={data?.categories} phoneCallId={data?.id} />
				{hasRecording && haveEnoughDuration && (
					<CallDetailsTranscription
						transcriptionId={data?.transcriptionId}
						phoneCall={data}
						onOpenAIFeedback={onOpenAIFeedback}
					/>
				)}
				<CallDetailsNotes phoneCallId={recentCallId} />
			</Group>
		</>
	);

	return (
		<div className={css(styleSheet.drawerContainer)}>
			{isLoading ? <LoadingSpinner type='login' /> : isEmpty ? renderEmpty : renderContent}
		</div>
	);
};
